import { packageException } from "../i18n/packageManagementPresentation";
import { usePackageManagementLanguage, packageFailure, packageFailureText, PackageLocalError, type PackageFailure } from "../i18n/packageManagementPresentation";
/**
 * Hook for fetching system-wide app installation status.
 *
 * Provides a Set of installed app IDs that the sidebar and other components
 * can use to conditionally show/hide nav items gated on `requiresApp`.
 *
 * Also provides `quickNavItems` — dynamically resolved sidebar navigation
 * entries for installed apps that have web UIs and are marked for quick nav.
 *
 * Endpoint: GET /api/system/apps/status
 * Backend caches results for 30s; this hook caches for 5 minutes to avoid
 * redundant network requests on navigation.
 */
import { useEffect, useRef, useState } from "react";

type SystemAppStatus = {
  appId: string;
  label: string;
  installed: boolean;
  category: string;
};

export type QuickNavItem = {
  id: string;
  label: string;
  icon: string;
  category: string;
  href: string;
  hrefType: "internal" | "external";
  /** Derived health status: healthy (active), stopped (!active), or unknown (no data). */
  status: "healthy" | "stopped" | "degraded" | "unknown";
  /** Installed version from user_software, or null if unknown. */
  installedVersion: string | null;
  /** Upstream/latest version, or null if unknown. */
  latestVersion: string | null;
  /** True when an update is available. */
  updateAvailable: boolean;
  /** Whether the upstream package is available for download. Defaults to true. */
  packageAvailable?: boolean;
  /** Whether the systemd unit is in active state. */
  active: boolean;
  /** Whether this app should show service status indicators (false for web-only apps). */
  showServiceStatus?: boolean;
  /** Systemd unit name pattern (e.g. "sonarr@{username}"), null for web-only apps. */
  serviceName?: string | null;
};

type SystemAppsResponse = {
  ok: boolean;
  data?: {
    apps: SystemAppStatus[];
    installedApps: string[];
    primaryDomain?: string;
    quickNavItems?: QuickNavItem[];
    hasMediaServer?: boolean;
  };
  message?: string;
};

type UseSystemAppsReturn = {
  /** Set of appId strings that are currently installed. */
  installedApps: Set<string>;
  /**
   * Primary server domain for global apps (webconsole, phpmyadmin).
   * Set when v4 runs on a different subdomain than the main server.
   * Undefined means use current window.location.hostname.
   */
  primaryDomain: string | undefined;
  /** Resolved quick nav items for the Software sidebar section. */
  quickNavItems: QuickNavItem[];
  /** True if any media server (emby, jellyfin, plex) is installed for ANY user on the system. */
  hasMediaServer: boolean;
  /** True during the initial fetch. */
  loading: boolean;
  /** Error message if the fetch failed. */
  error: string | null;
};

/** How long to cache the result client-side (5 minutes). */
const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000;

// Module-level cache so all consumers share a single result.
let cachedInstalledApps: Set<string> | null = null;
let cachedPrimaryDomain: string | undefined = undefined;
let cachedQuickNavItems: QuickNavItem[] | null = null;
let cachedHasMediaServer = false;
let cachedAt = 0;

// Module-level status cache keyed by app ID, populated by useSystemApps fetch.
// Consumed by useAppStatus to provide real status data to AppStatusDot.
let cachedAppStatusMap: Map<string, QuickNavItem> = new Map();

// Invalidation subscription — allows mounted hooks to re-fetch when cache is cleared.
// Without this, invalidateSystemAppsCache() clears the module cache but no React
// state changes, so mounted components never re-render to pick up fresh data.
type InvalidationListener = () => void;
const invalidationListeners = new Set<InvalidationListener>();

/** Subscribe to cache invalidation events. Returns unsubscribe function. */
const subscribeInvalidation = (listener: InvalidationListener): (() => void) => {
  invalidationListeners.add(listener);
  return () => { invalidationListeners.delete(listener); };
};

/** Get cached status data for a specific app. Returns undefined if not cached. */
export const getAppStatusFromCache = (appId: string): QuickNavItem | undefined => {
  return cachedAppStatusMap.get(appId);
};

/**
 * Clear the module-level cache and notify all mounted hooks to re-fetch.
 * This triggers a live update of the sidebar nav — e.g., after WireGuard
 * install/remove, the VPN Control item appears/disappears without a page refresh.
 */
export const invalidateSystemAppsCache = (): void => {
  cachedInstalledApps = null;
  cachedQuickNavItems = null;
  cachedAt = 0;
  // Notify all mounted useSystemApps hooks to trigger a re-fetch
  for (const listener of invalidationListeners) {
    listener();
  }
};

export const useSystemApps = (): UseSystemAppsReturn => {
  usePackageManagementLanguage();
  const [installedApps, setInstalledApps] = useState<Set<string>>(
    () => cachedInstalledApps ?? new Set()
  );
  const [primaryDomain, setPrimaryDomain] = useState<string | undefined>(
    () => cachedPrimaryDomain
  );
  const [quickNavItems, setQuickNavItems] = useState<QuickNavItem[]>(
    () => cachedQuickNavItems ?? []
  );
  const [hasMediaServer, setHasMediaServer] = useState(cachedHasMediaServer);
  const [loading, setLoading] = useState(!cachedInstalledApps);
  const [error, setError] = useState<PackageFailure | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Invalidation trigger — incremented when invalidateSystemAppsCache() is called,
  // causing the fetch effect to re-run and pick up fresh data from the API.
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Subscribe to cache invalidation events from other components
  useEffect(() => {
    return subscribeInvalidation(() => setFetchTrigger((n) => n + 1));
  }, []);

  useEffect(() => {
    let mounted = true;

    // Return cached result if still fresh
    if (cachedInstalledApps && Date.now() - cachedAt < CLIENT_CACHE_TTL_MS) {
      setInstalledApps(cachedInstalledApps);
      setPrimaryDomain(cachedPrimaryDomain);
      setQuickNavItems(cachedQuickNavItems ?? []);
      setHasMediaServer(cachedHasMediaServer);
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch("/api/system/apps/status", {
          credentials: "include",
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });

        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw packageException(body, { key: "requestFailed", values: { status: res.status } });
        }

        const payload = (await res.json()) as SystemAppsResponse;
        if (!mounted) return;

        if (!payload.ok || !payload.data) {
          throw payload.message ? packageException(payload, payload.message) : new PackageLocalError("invalidResponse");
        }

        const result = new Set(payload.data.installedApps);
        const navItems = payload.data.quickNavItems ?? [];
        const mediaServerPresent = payload.data.hasMediaServer ?? false;
        cachedInstalledApps = result;
        cachedPrimaryDomain = payload.data.primaryDomain;
        cachedQuickNavItems = navItems;
        cachedHasMediaServer = mediaServerPresent;
        cachedAt = Date.now();

        // Populate app status cache for useAppStatus consumption
        cachedAppStatusMap = new Map(navItems.map((item) => [item.id, item]));

        setInstalledApps(result);
        setPrimaryDomain(payload.data.primaryDomain);
        setQuickNavItems(navItems);
        setHasMediaServer(mediaServerPresent);
        setError(null);
      } catch (err) {
        if (!mounted || controller.signal.aborted) return;
        const message = packageFailure(err, "systemAppsFailed");
        setError(message);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchStatus();

    return () => {
      mounted = false;
      abortRef.current?.abort();
    };
  }, [fetchTrigger]);

  return { installedApps, primaryDomain, quickNavItems, hasMediaServer, loading, error: packageFailureText(error) };
};
